import React from "react";
import { Navigate, Routes, Route } from "react-router-dom";

// Layout and shared components
import { Layout } from "./components/Layout";
import Home from "./components/Home";
import { Auth0 } from "./components/shared/Auth0";
import Auth0Callback from "./components/shared/Auth0Callback";

// Admin components
import AdminAccountsContainer from "./components/Admin/AdminAccountsContainer";
import AdminSharedAccountCredentials from "./components/Admin/AdminSharedAccountCredentials";
import AdminClientUsers from "./components/Admin/AdminClientUsers";
import AdminAccessControlGroups from "./components/Admin/AdminAccessControlGroups";
import AdminTenetsContainer from "./components/Admin/AdminTenetsContainer";

// Key Requests components
import KeyRequests from "./components/KeyRequests/KeyRequests";
import GuestInvite from "./components/KeyRequests/GuestInvite";
import FromMeInvites from "./components/KeyRequests/FromMeInvites";
import FromMeAccessLog from "./components/KeyRequests/FromMeAccessLog";

// Other components
import AccessControlGroupDetail from "./components/AccessControlGroups/AccessControlGroup";
import ScreenInvite from "./components/ScreenInvites/ScreenInvite";
import LegacySites from "./components/Sites/Sites";
import AdminSitesRouter from "./components/AdminSites/Sites";
import LegacyLocks from "./components/Locks/Locks";
import Locks from "./components/AdminLocks/Locks";
import Groups from "./components/AdminGroups/Groups";
import Guests from "./components/AdminGuests/Guests";
import Login from "components/login/Login";
import auth from "shared/Auth0";
import { QueryClient } from "@tanstack/react-query";
import { getUserMe } from "helpers/useGetUser";
import LogoutPage from "components/Logout/Logout";
import ErrorBoundary from "shared/context/ErrorBoundary";
import Properties from "./components/Properties/Properties";
import PropertyPage from "components/Properties/Property";
import { getLocksViewPath } from "shared/utils/locksViewPreference";
import { getGroupsViewPath } from "shared/utils/groupsViewPreference";
import { getSitesViewPath } from "shared/utils/sitesViewPreference";
import { UseLivlyLeaseUser } from "shared/api/LivlyUserProvider";
import { isSuperUser } from "shared/api/isSuperUser";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      gcTime: 1000 * 60 * 60, // garbage collection time of 1 hour
    },
  },
});

const userMeQuery = () => ({
  queryKey: ["user", "me"],
  queryFn: async () => getUserMe(),
});

const loader = (queryClient: QueryClient) => () =>
  queryClient.fetchQuery({
    ...userMeQuery(),
    staleTime: 1000 * 60 * 2,
  });

function LandingPage() {
  if (auth.isAuthenticated()) {
    return <Navigate to="/admin" />;
  } else {
    return <Navigate to="/login" />;
  }
}

function LocksViewRedirect() {
  const user = UseLivlyLeaseUser();
  const isSuperUserPermission = isSuperUser(user);
  return <Navigate to={getLocksViewPath(undefined, isSuperUserPermission)} replace />;
}

function GroupsViewRedirect() {
  const user = UseLivlyLeaseUser();
  const isSuperUserPermission = isSuperUser(user);
  return <Navigate to={getGroupsViewPath(undefined, isSuperUserPermission)} replace />;
}

function SitesViewRedirect() {
  const user = UseLivlyLeaseUser();
  const isSuperUserPermission = isSuperUser(user);
  return <Navigate to={getSitesViewPath(undefined, isSuperUserPermission)} replace />;
}

function App() {
  return (
    <Routes>
      {/* Public routes - No Layout */}
      <Route path="/login" element={<Login />} />
      <Route path="/logout" element={<LogoutPage />} />
      <Route path="/callback" element={<Auth0Callback />} />
      {/* Home */}
      <Route path="/" element={<LandingPage />} />
      <Route errorElement={<ErrorBoundary />}>
        {/* Admin routes with Layout */}
        <Route
          path="/admin/*"
          element={
            <Layout>
              <Auth0 shouldRenderLogin={true} isForTenantUser={true}>
                <Routes>
                  {/* Admin home (default /admin) */}
                  <Route
                    index
                    id="homeLoader"
                    loader={loader(queryClient)}
                    element={<Home />}
                  />
                  <Route
                    path="accessControlGroups-switch"
                    element={<GroupsViewRedirect />}
                  />
                  <Route path="accessControlGroups/*" element={<Groups />} />
                  <Route
                    path="accessControlGroups-legacy"
                    element={<AdminAccessControlGroups />}
                  />
                  <Route
                    path="accessControlGroups-legacy/:accessControlGroupId"
                    element={<AccessControlGroupDetail />}
                  />
                  <Route
                    path="sharedCredentials"
                    element={<AdminSharedAccountCredentials />}
                  />
                  <Route path="tenants" element={<AdminTenetsContainer />} />
                  <Route path="accounts" element={<AdminAccountsContainer />} />
                  <Route path="clientUsers" element={<AdminClientUsers />} />
                  {/* Moved tenant user routes under /admin */}
                  <Route path="keyRequests" element={<KeyRequests />} />
                  <Route path="myInvite" element={<GuestInvite />} />
                  <Route path="myScreenInvite" element={<ScreenInvite />} />
                  <Route path="fromMeInvites" element={<FromMeInvites />} />
                  <Route path="fromMeAccessLog" element={<FromMeAccessLog />} />
                  <Route path="sites-switch" element={<SitesViewRedirect />} />
                  <Route path="sites/*" element={<AdminSitesRouter />} />
                  <Route path="sites-legacy/*" element={<LegacySites />} />
                  <Route path="locks-switch" element={<LocksViewRedirect />} />
                  <Route path="locks/*" element={<Locks />} />
                  <Route path="locks-legacy/*" element={<LegacyLocks />} />
                  <Route
                    path="locks2/*"
                    element={<Navigate to="/admin/locks" replace />}
                  />
                  <Route path="properties" element={<Properties />} />
                  <Route path="properties/:externalId/*" element={<PropertyPage />} />
                  <Route path="guests/*" element={<Guests />} />
                </Routes>
              </Auth0>
            </Layout>
          }
        />
        {/* Guest routes with Layout but NOT tenant users. Require login, but user should NOT belong to a tenant. */}
        <Route
          path="/admin/guest/*"
          element={
            <Layout>
              <Auth0 shouldRenderLogin={true} isForTenantUser={false}>
                <Routes>
                  <Route path="myInvite" element={<GuestInvite />} />
                  <Route path="myScreenInvite" element={<ScreenInvite />} />
                  <Route path="fromMeInvites" element={<FromMeInvites />} />
                  <Route path="fromMeAccessLog" element={<FromMeAccessLog />} />
                </Routes>
              </Auth0>
            </Layout>
          }
        />
      </Route>
      {/* Legacy redirects to new /admin routes */}
      <Route
        path="/keyRequests"
        element={<Navigate to="/admin/keyRequests" replace />}
      />
      <Route
        path="/KeyRequests"
        element={<Navigate to="/admin/keyRequests" replace />}
      />
      <Route
        path="/myInvite"
        element={<Navigate to="/admin/myInvite" replace />}
      />
      <Route
        path="/myScreenInvite"
        element={<Navigate to="/admin/myScreenInvite" replace />}
      />
      <Route
        path="/fromMeInvites"
        element={<Navigate to="/admin/fromMeInvites" replace />}
      />
      <Route
        path="/fromMeAccessLog"
        element={<Navigate to="/admin/fromMeAccessLog" replace />}
      />

      {/* Guest legacy redirects under /admin */}
      <Route
        path="/guest/myInvite"
        element={<Navigate to="/admin/guest/myInvite" replace />}
      />
      <Route
        path="/guest/myScreenInvite"
        element={<Navigate to="/admin/guest/myScreenInvite" replace />}
      />
      <Route
        path="/guest/fromMeInvites"
        element={<Navigate to="/admin/guest/fromMeInvites" replace />}
      />
      <Route
        path="/guest/fromMeAccessLog"
        element={<Navigate to="/admin/guest/fromMeAccessLog" replace />}
      />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default App;
